import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  TrendingDown,
  TrendingUp,
  Server,
  Cloud,
  Layers,
  Info,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Sparkles,
  ShoppingBag,
} from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";

// Modernized color palette matching deep space aesthetics
const C = {
  grid: "rgba(148,163,184,0.1)",
  axis: "#64748B",
  text: "#CBD5E1",
};

const TOP_N = 12;

const fmtUSD = (n) =>
  `$${(Number(n) || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const fmtAxis = (n) => {
  const a = Math.abs(n);
  if (a >= 1000) return `$${(n / 1000).toFixed(a >= 10000 ? 0 : 1)}k`;
  return `$${Math.round(n)}`;
};

// Strictly sort High-to-Low and fold into top-N + aggregated "Other"
function foldTop(services, key = "awsCost") {
  const sorted = [...services].sort((a, b) => b[key] - a[key]);
  if (sorted.length <= TOP_N) return sorted;
  const head = sorted.slice(0, TOP_N);
  const tail = sorted.slice(TOP_N);
  const other = tail.reduce(
    (acc, s) => ({
      service: "Other Mapped Services",
      gcpEquivalent: "Various GCP Services",
      awsBaseCost: acc.awsBaseCost + (s.awsBaseCost || 0),
      awsSavings: acc.awsSavings + (s.awsSavings || 0),
      awsCredits: acc.awsCredits + (s.awsCredits || 0),
      awsCost: acc.awsCost + s.awsCost,
      gcpCost: acc.gcpCost + s.gcpCost,
      savings: acc.savings + s.savings,
      _count: acc._count + 1,
    }),
    { awsBaseCost: 0, awsSavings: 0, awsCredits: 0, awsCost: 0, gcpCost: 0, savings: 0, _count: 0 }
  );
  return [...head, other];
}

// Custom Dynamic Bar that "Pops Out" and Glows when active
const DynamicBar = (props) => {
  const { x, y, width, height, fill, index, activeIndex, isAws } = props;
  const isActive = index === activeIndex;

  // We only care if height is invalid. We will force width to be at least 2px for $0 items.
  if (height <= 0) return null;

  // Force a minimum visible width of 2px so $0 cost items still show a sliver
  const minWidth = Math.max(width, 2);

  const glowColor = isAws ? "rgba(245,158,11,0.6)" : "rgba(6,182,212,0.6)";
  const strokeColor = isAws ? "#FCD34D" : "#67E8F9";

  const targetY = isActive ? y - 3 : y;
  const targetHeight = isActive ? height + 6 : height;
  const targetWidth = isActive ? minWidth + 6 : minWidth;

  return (
    <rect
      x={x}
      y={targetY}
      width={targetWidth}
      height={targetHeight}
      fill={fill}
      rx={4}
      stroke={isActive ? strokeColor : "transparent"}
      strokeWidth={isActive ? 2 : 0}
      style={{
        transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
        filter: isActive ? `drop-shadow(0 0 15px ${glowColor})` : "none",
        cursor: "pointer",
      }}
    />
  );
};

// Custom Y-Axis label that triggers tooltip and bar glow on hover
const CustomYAxisTick = (props) => {
  const { x, y, payload, index, activeIndex, setActiveIndex, dataList, setAxisHoverItem, darkMode } = props;
  const isActive = index === activeIndex;
  const item = dataList?.[index];

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={4}
        textAnchor="end"
        fill={isActive ? (darkMode ? "#FFFFFF" : "#0f172a") : (darkMode ? C.text : "#64748b")}
        fontSize={12}
        fontWeight={isActive ? 800 : 500}
        style={{ cursor: "pointer", transition: "all 0.2s ease" }}
        onMouseEnter={() => {
          setActiveIndex(index);
          if (item) setAxisHoverItem(item);
        }}
        onMouseLeave={() => {
          setActiveIndex(null);
          setAxisHoverItem(null);
        }}
      >
        {payload.value}
      </text>
    </g>
  );
};

// Reusable Glass Tooltip Component with Cost Breakdown
const TooltipCard = ({ item, darkMode }) => {
  if (!item) return null;

  // Only show the base usage row if there are actual savings or credits.
  const showBaseUsage = item.awsSavings > 0 || item.awsCredits > 0;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95, y: 5 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.15 }}
      className={`rounded-2xl border px-5 py-4 min-w-[340px] pointer-events-none ${darkMode ? "border-white/10 bg-[#0B132B]/95 shadow-[0_15px_40px_rgba(0,0,0,0.85)] backdrop-blur-xl" : "border-slate-200 bg-white shadow-xl"}`}
    >
      <div className={`mb-4 flex flex-col gap-2.5 border-b pb-4 ${darkMode ? "border-white/10" : "border-slate-100"}`}>
        <div>
          <span className={`text-[10px] font-bold flex items-center gap-3 uppercase tracking-widest mb-1.5 ${darkMode ? "text-amber-400" : "text-amber-500"}`}>
            <AwsIcon className="h-3.5 w-auto mr-1.5" /> AWS Service
          </span>
          <span className={`text-base font-bold tracking-tight leading-snug ${darkMode ? "text-white" : "text-slate-900"}`}>{item.service}</span>
        </div>
        {item.gcpEquivalent && item.gcpEquivalent !== "N/A" && (
          <div className="mt-1">
            <span className={`text-[10px] font-bold flex items-center gap-1.5 uppercase tracking-widest mb-1.5 ${darkMode ? "text-cyan-400" : "text-cyan-500"}`}>
              <GcpIcon className="h-3.5 w-auto" /> GCP Equivalent
            </span>
            <span className={`text-base font-bold tracking-tight leading-snug ${darkMode ? "text-white" : "text-slate-900"}`}>{item.gcpEquivalent}</span>
          </div>
        )}
      </div>

      <div className="space-y-4">
        {/* AWS Breakdown */}
        <div className={`rounded-xl p-3 border space-y-2 ${darkMode ? "bg-black/20 border-white/5" : "bg-slate-50 border-slate-200"}`}>
          <div className={`text-[10px] font-bold uppercase tracking-widest mb-2 border-b pb-1 ${darkMode ? "text-slate-400 border-white/5" : "text-slate-500 border-slate-200"}`}>
            AWS Cost Breakdown
          </div>
          {/*EC2 Description */}
          {item.service === "Amazon Elastic Compute Cloud" && (
            <div className="text-[11px] italic text-slate-400 mb-2 leading-tight">
              *Includes EC2 (On-Demand, Spot, Reserved), NAT Gateway, and EBS usage.
            </div>
          )}

          {/*Bedrock Description */}
          {item.service === "Amazon Bedrock (Claude AI Models)" && (
            <div className="text-[11px] italic text-slate-400 mb-2 leading-tight">
              *Includes Claude AI model usage via AWS Marketplace.
            </div>
          )}

          {/* VPC Description */}
          {item.service === "Virtual Private Cloud" && (
            <div className="text-[11px] italic text-slate-400 mb-2 leading-tight">
              *Includes VPC Peering, VPN, and Gateway data usage.
            </div>
          )}

          {/* Conditional Base Usage Row */}
          {showBaseUsage && (
            <div className="flex items-center justify-between text-[12px] text-slate-300">
              <span>Base Usage Cost</span>
              <span className="font-medium">{fmtUSD(item.awsBaseCost)}</span>
            </div>
          )}

          {item.awsSavings > 0 && (
            <div className="flex items-center justify-between text-[12px] text-emerald-400">
              <span>Savings / Discounts</span>
              <span className="font-medium">+{fmtUSD(item.awsSavings)}</span>
            </div>
          )}
          {item.awsCredits > 0 && (
            <div className="flex items-center justify-between text-[12px] text-purple-400">
              <span>Credits Applied</span>
              <span className="font-medium">+{fmtUSD(item.awsCredits)}</span>
            </div>
          )}
          <div className={`flex items-center justify-between gap-8 text-[13px] pt-2 mt-1 border-t ${darkMode ? "border-white/10" : "border-slate-200"}`}>
            <span className={`flex items-center gap-2.5 font-bold ${darkMode ? "text-amber-400" : "text-amber-600"}`}>
              <span className={`inline-block h-2 w-2 rounded-full ${darkMode ? "bg-amber-500 shadow-[0_0_8px_#F59E0B]" : "bg-amber-500"}`} />
              Total AWS Cost
            </span>
            <span className={`font-extrabold tabular-nums text-sm ${darkMode ? "text-white" : "text-slate-900"}`}>
              {fmtUSD(item.awsCost)}
            </span>
          </div>
        </div>

        {/* GCP Total */}
        <div className="flex items-center justify-between gap-8 text-[13px] px-1">
          <span className={`flex items-center gap-2.5 font-bold ${darkMode ? "text-cyan-400" : "text-cyan-600"}`}>
            <span className={`inline-block h-2 w-2 rounded-full ${darkMode ? "bg-cyan-400 shadow-[0_0_8px_#22D3EE]" : "bg-cyan-500"}`} />
            Estimated GCP Cost
          </span>
          <span className={`font-extrabold tabular-nums text-sm ${darkMode ? "text-white" : "text-slate-900"}`}>
            {fmtUSD(item.gcpCost)}
          </span>
        </div>
      </div>
    </motion.div>
  );
};

const AwsIcon = ({ className }) => (
  <img
    src="/images/amazon_icon.png"
    alt="AWS Logo"
    className={`object-contain scale-[1.8] ${className}`}
  />
);

const GcpIcon = ({ className }) => (
  <img
    src="/images/google_icon.png"
    alt="GCP Logo"
    className={`object-contain ${className}`}
  />
);

// Interactive, animated KPI Card
const KpiCard = ({ icon: Icon, label, value, sub, tone = "neutral", darkMode }) => {
  const configs = {
    aws: {
      ring: "border-amber-500/30 text-amber-400 bg-amber-500/10",
      glow: "hover:border-amber-500/50 hover:shadow-[0_0_25px_rgba(245,158,11,0.15)]",
      dot: "bg-amber-400"
    },
    gcp: {
      ring: "border-cyan-500/30 text-cyan-400 bg-cyan-500/10",
      glow: "hover:border-cyan-500/50 hover:shadow-[0_0_25px_rgba(6,182,212,0.15)]",
      dot: "bg-cyan-400"
    },
    save: {
      ring: "border-emerald-500/30 text-emerald-400 bg-emerald-500/10",
      glow: "hover:border-emerald-500/50 hover:shadow-[0_0_25px_rgba(16,185,129,0.15)]",
      dot: "bg-emerald-400"
    },
    neutral: {
      ring: "border-indigo-500/30 text-indigo-400 bg-indigo-500/10",
      glow: "hover:border-indigo-500/50 hover:shadow-[0_0_25px_rgba(99,102,241,0.15)]",
      dot: "bg-indigo-400"
    },
  };

  const c = configs[tone] || configs.neutral;

  return (
    <motion.div
      whileHover={{ y: -4, scale: 1.02 }}
      transition={{ type: "spring", stiffness: 400, damping: 25 }}
      className={`relative overflow-hidden rounded-2xl p-5 border transition-all duration-300 group cursor-default ${darkMode ? "border-white/5 bg-[#1E293B]/40 backdrop-blur-md" : "border-slate-200 bg-white shadow-sm hover:shadow-md"} ${c.glow}`}
    >
      <div className="absolute top-4 right-4 flex h-2 w-2">
        <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${c.dot}`}></span>
        <span className={`relative inline-flex rounded-full h-2 w-2 ${c.dot}`}></span>
      </div>

      <div className="relative z-10 flex items-center gap-3">
        <div className={`flex h-10 w-10 items-center justify-center rounded-xl border ${c.ring} shadow-inner`}>
          <Icon className="h-5 w-auto max-w-[30px]" />
        </div>
        <span className={`text-xs font-bold uppercase tracking-widest transition-colors ${darkMode ? "text-slate-400 group-hover:text-slate-300" : "text-slate-400 group-hover:text-slate-500"}`}>
          {label}
        </span>
      </div>
      <div className={`relative z-10 mt-4 text-3xl font-extrabold tabular-nums tracking-tight ${darkMode ? "text-white" : "text-slate-900"}`}>
        {value}
      </div>
      {sub && <div className="relative z-10 mt-1.5 text-xs font-medium text-slate-500 group-hover:text-slate-400 transition-colors">{sub}</div>}
    </motion.div>
  );
};

const DashboardModal = ({ chartData, onClose, darkMode = true }) => {
  const [showAll, setShowAll] = useState(false);
  const [showUnmapped, setShowUnmapped] = useState(true);
  const [showMarketplace, setShowMarketplace] = useState(false);

  // Track active hover index and Y-axis text hover item
  const [activeIndex, setActiveIndex] = useState(null);
  const [axisHoverItem, setAxisHoverItem] = useState(null);

  // 1. Add this robust number parser inside your component
  const parseNum = (val) => {
    if (val === undefined || val === null || val === "N/A") return 0;
    if (typeof val === "number") return val;
    // Strip out $, commas, and spaces before converting strings to numbers
    const cleanStr = String(val).replace(/[$, ]/g, "");
    return Number(cleanStr) || 0;
  };

  // 2. Update your extractor to use nullish coalescing (??) and check multiple standard keys
  const extractServiceCosts = (s) => {
    const baseCost = parseNum(s.aws_base_cost ?? s.aws_cost_usd ?? s.aws_cost ?? s.awsCost ?? s.awsBaseCost);
    const savings = parseNum(s.aws_savings ?? s.aws_savings_usd ?? s.savings ?? s.awsSavings);
    const credits = parseNum(s.aws_credits ?? s.aws_credits_usd ?? s.credits ?? s.awsCredits);

    const totalAws = baseCost + savings + credits;
    return { baseCost, savings, credits, totalAws };
  };

  // 3. Update mappedServices to be more forgiving with missing keys
  const mappedServices = useMemo(() => {
    const rawList = chartData?.services || [];
    return rawList.map((s, idx) => {
      const { baseCost, savings, credits, totalAws } = extractServiceCosts(s);

      const rawGcpCost = s.gcp_cost ?? s.gcp_cost_usd ?? s.gcpCost;
      const gcpCost = (rawGcpCost !== "N/A" && rawGcpCost != null) ? parseNum(rawGcpCost) : 0;

      return {
        id: s.id || `mapped-${idx}`,
        service: s.aws_service || s.service_name || s.service || "AWS Service",
        gcpEquivalent: s.gcp_equivalent || s.gcp_service || "N/A",
        awsBaseCost: baseCost,
        awsSavings: savings,
        awsCredits: credits,
        awsCost: totalAws,
        gcpCost: gcpCost,
        savings: totalAws - gcpCost,
      };
    }).filter(s => s.awsCost > 0 || s.gcpCost > 0);
  }, [chartData]);

  // 4. Update Unmapped Native Services
  const unmappedServices = useMemo(() => {
    const rawList = chartData?.unmapped_native_services || [];
    return rawList.map((s, idx) => {
      const { baseCost, savings, credits, totalAws } = extractServiceCosts(s);
      return {
        id: `unmapped-${idx}`,
        service: s.aws_service || s.service_name || s.service,
        awsBaseCost: baseCost,
        awsSavings: savings,
        awsCredits: credits,
        awsCost: totalAws
      };
    }).filter(s => s.awsCost > 0 || s.awsBaseCost > 0);
  }, [chartData]);

  // 5. Update Marketplace Services
  const marketplaceServices = useMemo(() => {
    const rawList = chartData?.marketplace_services || [];
    return rawList.map((s, idx) => {
      const { baseCost, savings, credits, totalAws } = extractServiceCosts(s);
      return {
        id: `mp-${idx}`,
        service: s.aws_service || s.service_name || s.service,
        awsBaseCost: baseCost,
        awsSavings: savings,
        awsCredits: credits,
        awsCost: totalAws
      };
    }).filter(s => s.awsCost > 0 || s.awsBaseCost > 0);
  }, [chartData]);

  // Calculate Marketplace Aggregates
  const marketplaceTotals = useMemo(() => {
    return marketplaceServices.reduce((acc, s) => ({
      baseCost: acc.baseCost + s.awsBaseCost,
      savings: acc.savings + s.awsSavings,
      credits: acc.credits + s.awsCredits,
      totalAws: acc.totalAws + s.awsCost
    }), { baseCost: 0, savings: 0, credits: 0, totalAws: 0 });
  }, [marketplaceServices]);

  // Overall Totals
  const totals = useMemo(() => {
    const rawTotals = chartData?.totals || {};
    const totalAws = rawTotals.total_aws_cost ?? mappedServices.reduce((sum, s) => sum + s.awsCost, 0);
    const totalGcp = rawTotals.total_gcp_cost ?? mappedServices.reduce((sum, s) => sum + s.gcpCost, 0);

    let grandTotalVal = rawTotals.invoice_grand_total;
    if (grandTotalVal === 0 || grandTotalVal == null) {
      grandTotalVal = rawTotals.invoice_total_savings ?? totalAws;
    }

    const finalSavings = grandTotalVal - totalGcp;
    const finalSavingsPct = grandTotalVal > 0 ? (finalSavings / grandTotalVal) * 100 : 0;

    const mappedCount = mappedServices.length;
    const pipelineCount = unmappedServices.length + marketplaceServices.length;
    const totalCount = mappedCount + pipelineCount;

    return {
      awsCost: totalAws,
      gcpCost: totalGcp,
      grandTotal: grandTotalVal,
      savings: finalSavings,
      savingsPct: finalSavingsPct,
      mappedCount,
      pipelineCount,
      totalCount
    };
  }, [chartData, mappedServices, unmappedServices, marketplaceServices]);

  // Graph Data Sorting (High to Low)
  const compareData = useMemo(() => {
    const sortedList = [...mappedServices].sort((a, b) => b.awsCost - a.awsCost);
    return showAll ? sortedList : foldTop(sortedList, "awsCost");
  }, [mappedServices, showAll]);

  // Calculate dynamic Y-Axis width based on the longest visible service name
  const dynamicYAxisWidth = useMemo(() => {
    if (!compareData || compareData.length === 0) return 120;
    const maxChars = Math.max(...compareData.map((d) => (d.service || "").length));

    // Roughly 7.5 pixels per character for a 12px font, plus 20px of padding.
    // We also set a minimum of 150 and maximum of 400 to keep the layout sane.
    const calculatedWidth = maxChars * 7.5 + 20;
    return Math.min(Math.max(calculatedWidth, 100), 280);
  }, [compareData]);

  const savingsPositive = totals.savings >= 0;
  const compareHeight = Math.max(260, compareData.length * 48 + 60);

  // Calculate dynamic vertical position for the tooltip based on the hovered row, clamped to prevent axis overflow
  const tooltipTop = useMemo(() => {
    if (activeIndex === null || !compareData.length) return 20;

    const rowHeight = (compareHeight - 40) / compareData.length;
    const targetCenterY = activeIndex * rowHeight + rowHeight / 2 + 10;
    const tooltipHeight = 260; // Estimated card height

    const clampedTop = Math.max(
      10,
      Math.min(targetCenterY - tooltipHeight / 2, compareHeight - tooltipHeight - 10)
    );

    return clampedTop;
  }, [activeIndex, compareHeight, compareData.length]);

  return createPortal(
    <AnimatePresence>
      <motion.div
        key="dash-overlay"
        initial={{ opacity: 0, backdropFilter: "blur(0px)" }}
        animate={{ opacity: 1, backdropFilter: "blur(8px)" }}
        exit={{ opacity: 0, backdropFilter: "blur(0px)" }}
        transition={{ duration: 0.3 }}
        className={`fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto p-3 sm:p-6 ${darkMode ? "bg-[#040914]/80" : "bg-slate-900/40"}`}
        onClick={onClose}
      >
        <motion.div
          key="dash-panel"
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.95 }}
          transition={{ type: "spring", stiffness: 300, damping: 28 }}
          onClick={(e) => e.stopPropagation()}
          className={`relative my-2 w-full max-w-[1100px] rounded-[2rem] border shadow-[0_0_50px_rgba(0,0,0,0.8)] ${darkMode ? "border-white/10" : "border-slate-200 bg-white"}`}
          style={darkMode ? { background: "linear-gradient(145deg, #0A1226 0%, #060B14 100%)" } : undefined}
        >
          {/* Header */}
          <div className={`relative z-20 flex items-center justify-between gap-4 border-b rounded-t-[2rem] px-5 py-5 backdrop-blur-xl sm:px-8 ${darkMode ? "border-white/5 bg-[#0A1226]/80" : "border-slate-200 bg-white/90"}`}>
            <div className="flex items-center gap-4">
              <div className="hidden sm:flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-cyan-400/20 to-blue-600/20 border border-cyan-400/30 shadow-[0_0_15px_rgba(6,182,212,0.2)]">
                <Sparkles className="h-6 w-6 text-cyan-400" />
              </div>
              <div>
                <h2 className={`text-xl font-extrabold tracking-tight sm:text-2xl flex items-center gap-2 ${darkMode ? "text-white" : "text-slate-900"}`}>
                  Migration TCO <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">Insights</span>
                </h2>
                <p className={`mt-1 text-xs font-medium ${darkMode ? "text-slate-400" : "text-slate-500"}`}>
                  Analysis derived from uploaded AWS Cost & Usage invoice • Figures in {chartData?.currency || "USD"}
                </p>
              </div>
            </div>
            <motion.button
              whileHover={{ rotate: 90, scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={onClose}
              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border transition-all hover:bg-rose-500/20 hover:border-rose-500/50 hover:text-rose-400 shadow-sm ${darkMode ? "border-white/10 bg-white/5 text-slate-400" : "border-slate-200 bg-slate-50 text-slate-500 hover:bg-rose-50"}`}
              title="Close"
            >
              <X className="h-5 w-5" />
            </motion.button>
          </div>

          <div className="space-y-8 px-5 py-8 sm:px-8 relative z-10">
            {/* KPI Row */}
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              <KpiCard
                icon={AwsIcon}
                label="AWS Total Cost"
                value={fmtUSD(totals.grandTotal)}
                tone="aws"
                darkMode={darkMode}
              />
              <KpiCard
                icon={GcpIcon}
                label="Estimated GCP Cost"
                value={fmtUSD(totals.gcpCost)}
                tone="gcp"
                darkMode={darkMode}
              />
              <KpiCard
                icon={savingsPositive ? TrendingDown : TrendingUp}
                label={savingsPositive ? "Estimated Savings" : "Estimated Increase"}
                value={fmtUSD(Math.abs(totals.savings))}
                tone="save"
                darkMode={darkMode}
              />
              <KpiCard
                icon={Layers}
                label="Total Services"
                value={totals.totalCount}
                tone="neutral"
                darkMode={darkMode}
              />
            </div>

            {/* AWS vs GCP Comparison Graph */}
            <section className={`rounded-[1.5rem] border p-5 sm:p-7 shadow-inner relative overflow-visible ${darkMode ? "border-white/5 bg-[#1E293B]/20" : "border-slate-200 bg-slate-50"}`}>
              <div className="absolute top-0 right-0 w-64 h-64 bg-cyan-500/5 rounded-full blur-[100px] pointer-events-none" />

              <div className="relative z-10 mb-3 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <h3 className={`text-lg font-bold tracking-tight ${darkMode ? "text-white" : "text-slate-900"}`}>
                  AWS vs GCP Cost by Service
                </h3>
                <div className={`flex items-center gap-6 text-xs font-bold tracking-widest uppercase px-4 py-2 rounded-xl border ${darkMode ? "text-slate-400 bg-white/5 border-white/5" : "text-slate-600 bg-white border-slate-200 shadow-sm"}`}>
                  <span className="flex items-center gap-2.5">
                    <span className="inline-block h-3 w-3 rounded-full bg-amber-500 shadow-[0_0_10px_rgba(245,158,11,0.6)]" />
                    AWS
                  </span>
                  <span className="flex items-center gap-2.5">
                    <span className="inline-block h-3 w-3 rounded-full bg-cyan-500 shadow-[0_0_10px_rgba(6,182,212,0.6)]" />
                    GCP
                  </span>
                </div>
              </div>

              <div className="relative z-10 mb-6 flex items-center justify-between gap-3">
                <p className="text-sm text-slate-400 font-medium">
                  {showAll
                    ? `Showing all ${mappedServices.length} mapped services (sorted High to Low).`
                    : `Showing top ${Math.min(TOP_N, mappedServices.length)} services by AWS spend (High to Low).`}
                </p>
                {mappedServices.length > TOP_N && (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    type="button"
                    onClick={() => setShowAll((v) => !v)}
                    className="shrink-0 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-4 py-2 text-xs font-bold text-cyan-400 transition-colors hover:bg-cyan-500/20 hover:border-cyan-400 shadow-[0_0_15px_rgba(6,182,212,0.1)]"
                  >
                    {showAll ? `Show top ${TOP_N}` : `Show all ${mappedServices.length}`}
                  </motion.button>
                )}
              </div>

              <div className={`relative z-10 mb-8 flex items-start gap-3 rounded-2xl border px-5 py-4 text-xs leading-relaxed shadow-sm ${darkMode ? "bg-indigo-500/10 border-indigo-500/20 text-indigo-200" : "bg-blue-50 border-blue-100 text-blue-800"}`}>
                <Info className={`mt-[2px] h-5 w-5 shrink-0 ${darkMode ? "text-indigo-400" : "text-blue-500"}`} />
                <p className="font-medium">
                  Figures reflect actual service usage/consumption cost and don't include taxes — see <span className={`font-bold ${darkMode ? "text-white" : "text-slate-900"}`}>AWS Total Cost</span> above for your actual billed total.
                </p>
              </div>

              {mappedServices.length > 0 ? (
                <div style={{ width: "100%", height: compareHeight }} className="relative z-10">

                  {/* Floating Popover Tooltip with dynamic clamping based on hovered row */}
                  <AnimatePresence>
                    {axisHoverItem && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.96 }}
                        animate={{ opacity: 1, scale: 1, top: tooltipTop }}
                        exit={{ opacity: 0, scale: 0.96 }}
                        transition={{ duration: 0.15, ease: "easeOut" }}
                        style={{ top: tooltipTop }}
                        className="absolute right-6 z-50 pointer-events-none"
                      >
                        <TooltipCard item={axisHoverItem} darkMode={darkMode} />
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <ResponsiveContainer>
                    <BarChart
                      layout="vertical"
                      data={compareData}
                      margin={{ top: 4, right: 30, left: 10, bottom: 4 }}
                      barGap={8}
                      onMouseMove={(state) => {
                        if (state.isTooltipActive && state.activeTooltipIndex !== undefined) {
                          setActiveIndex(state.activeTooltipIndex);
                          setAxisHoverItem(compareData[state.activeTooltipIndex]);
                        } else {
                          setActiveIndex(null);
                          setAxisHoverItem(null);
                        }
                      }}
                      onMouseLeave={() => {
                        setActiveIndex(null);
                        setAxisHoverItem(null);
                      }}
                    >
                      <defs>
                        <linearGradient id="colorAws" x1="0" y1="0" x2="1" y2="0">
                          <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.85} />
                          <stop offset="100%" stopColor="#b45309" stopOpacity={1} />
                        </linearGradient>
                        <linearGradient id="colorGcp" x1="0" y1="0" x2="1" y2="0">
                          <stop offset="0%" stopColor="#22d3ee" stopOpacity={0.8} />
                          <stop offset="100%" stopColor="#0284c7" stopOpacity={1} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid
                        horizontal={false}
                        stroke={C.grid}
                        strokeDasharray="4 4"
                      />
                      <XAxis
                        type="number"
                        tickFormatter={fmtAxis}
                        tick={{ fill: C.axis, fontSize: 12, fontWeight: 600 }}
                        axisLine={{ stroke: C.grid }}
                        tickLine={false}
                      />
                      <YAxis
                        type="category"
                        dataKey="service"
                        width={dynamicYAxisWidth}
                        axisLine={false}
                        tickLine={false}
                        tick={
                          <CustomYAxisTick
                            darkMode={darkMode}
                            activeIndex={activeIndex}
                            setActiveIndex={setActiveIndex}
                            dataList={compareData}
                            setAxisHoverItem={setAxisHoverItem}
                          />
                        }
                      />
                      <Tooltip
                        cursor={{ fill: darkMode ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)" }}
                        content={() => null}
                      />
                      <Bar
                        dataKey="awsCost"
                        name="AWS"
                        fill="url(#colorAws)"
                        isAnimationActive={true}
                        animationDuration={1200}
                        shape={<DynamicBar activeIndex={activeIndex} isAws={true} />}
                      />
                      <Bar
                        dataKey="gcpCost"
                        name="GCP"
                        fill="url(#colorGcp)"
                        isAnimationActive={true}
                        animationDuration={1200}
                        shape={<DynamicBar activeIndex={activeIndex} isAws={false} />}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className={`flex h-48 flex-col items-center justify-center rounded-2xl border border-dashed ${darkMode ? "border-white/10 bg-white/[0.01] text-slate-400" : "border-slate-300 bg-slate-100/50 text-slate-500"}`}>
                  <AlertCircle className="mb-3 h-8 w-8 text-slate-500" />
                  <p className="text-sm font-semibold">No mapped services found in this report.</p>
                </div>
              )}
            </section>

            {/* Strategic "Service Mapping Coming Soon" Section */}
            {/* 
            {(unmappedServices.length > 0 || marketplaceServices.length > 0) && (
              <section className="rounded-[1.5rem] border border-white/5 bg-[#1E293B]/30 p-5 sm:p-7 shadow-inner relative overflow-hidden">
                <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-indigo-500/5 rounded-full blur-[100px] pointer-events-none" />

                <div
                  className="relative z-10 flex cursor-pointer items-center justify-between group"
                  onClick={() => setShowUnmapped((v) => !v)}
                >
                  <div className="flex items-center gap-3.5">
                    <div className="flex items-center justify-center h-10 w-10 rounded-xl bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 shadow-[0_0_15px_rgba(99,102,241,0.1)]">
                      <Layers className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white tracking-tight">
                        Service Mapping Coming Soon
                      </h3>
                    </div>
                  </div>
                  <button
                    className="flex h-9 w-9 items-center justify-center rounded-full bg-white/5 text-slate-400 group-hover:bg-white/10 group-hover:text-white transition-colors border border-white/5"
                  >
                    {showUnmapped ? (
                      <ChevronUp className="h-5 w-5" />
                    ) : (
                      <ChevronDown className="h-5 w-5" />
                    )}
                  </button>
                </div>

                <AnimatePresence>
                  {showUnmapped && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="relative z-10 mt-6 overflow-hidden rounded-2xl border border-white/10 bg-[#0F172A]/80 backdrop-blur-md shadow-xl"
                    >
                      <table className="w-full text-left text-sm text-slate-300">
                        <thead className="border-b border-white/10 bg-white/[0.02] text-xs font-bold uppercase tracking-widest text-slate-400">
                          <tr>
                            <th className="px-6 py-4 w-[30%]">Identified Service</th>
                            <th className="px-6 py-4 text-right">Pre-Tax Charge</th>
                            <th className="px-6 py-4 text-right">Savings</th>
                            <th className="px-6 py-4 text-right">Credits</th>
                            <th className="px-6 py-4 text-right">Total AWS Cost</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-white/[0.05]">

                          {unmappedServices.map((srv) => (
                            <tr key={srv.id} className="group transition-colors hover:bg-white/[0.03]">
                              <td className="px-6 py-4 font-semibold text-slate-200 transition-colors group-hover:text-white">
                                <div className="flex items-center gap-2">
                                  <span className="truncate">{srv.service}</span>
                                </div>
                              </td>
                              <td className="px-6 py-4 text-right tabular-nums text-slate-300">
                                {fmtUSD(srv.awsBaseCost)}
                              </td>
                              <td className={`px-6 py-4 text-right tabular-nums ${srv.awsSavings > 0 ? "text-emerald-400" : "text-slate-600"}`}>
                                {srv.awsSavings > 0 ? `${fmtUSD(srv.awsSavings)}` : "-"}
                              </td>
                              <td className={`px-6 py-4 text-right tabular-nums ${srv.awsCredits > 0 ? "text-purple-400" : "text-slate-600"}`}>
                                {srv.awsCredits > 0 ? `${fmtUSD(srv.awsCredits)}` : "-"}
                              </td>
                              <td className="px-6 py-4 text-right font-bold tabular-nums text-white">
                                {fmtUSD(srv.awsCost)}
                              </td>
                            </tr>
                          ))}

                          {marketplaceServices.length > 0 && (
                            <React.Fragment>
                              <tr
                                onClick={() => setShowMarketplace(!showMarketplace)}
                                className="group cursor-pointer transition-colors hover:bg-white/[0.03]"
                              >
                                <td className="px-6 py-4 font-semibold text-slate-200 transition-colors group-hover:text-white">
                                  <div className="flex items-center gap-2">
                                    <ShoppingBag className="w-4 h-4 text-purple-400 mr-1 shrink-0" />
                                    <span className="truncate">Marketplace Services</span>
                                    <span className="ml-2 rounded bg-purple-500/20 px-2 py-0.5 text-[10px] text-purple-300 whitespace-nowrap">
                                      {marketplaceServices.length} items
                                    </span>
                                    {showMarketplace ? <ChevronUp className="w-4 h-4 ml-auto text-slate-500 shrink-0" /> : <ChevronDown className="w-4 h-4 ml-auto text-slate-500 shrink-0" />}
                                  </div>
                                </td>
                                <td className="px-6 py-4 text-right tabular-nums text-slate-300">
                                  {fmtUSD(marketplaceTotals.baseCost)}
                                </td>
                                <td className={`px-6 py-4 text-right tabular-nums ${marketplaceTotals.savings > 0 ? "text-emerald-400" : "text-slate-600"}`}>
                                  {marketplaceTotals.savings > 0 ? `${fmtUSD(marketplaceTotals.savings)}` : "-"}
                                </td>
                                <td className={`px-6 py-4 text-right tabular-nums ${marketplaceTotals.credits > 0 ? "text-purple-400" : "text-slate-600"}`}>
                                  {marketplaceTotals.credits > 0 ? `${fmtUSD(marketplaceTotals.credits)}` : "-"}
                                </td>
                                <td className="px-6 py-4 text-right font-bold tabular-nums text-white">
                                  {fmtUSD(marketplaceTotals.totalAws)}
                                </td>
                              </tr>

                              {showMarketplace && marketplaceServices.map((srv) => (
                                <tr key={srv.id} className="group bg-[#0A1226]/50 border-b border-white/5 transition-colors hover:bg-white/[0.03] last:border-0">
                                  <td className="px-6 py-4 pl-12 font-semibold text-[13px] text-slate-400 transition-colors group-hover:text-white">
                                    <div className="flex items-center gap-2">
                                      <div className="w-1.5 h-1.5 rounded-full bg-purple-500/50 shrink-0" />
                                      <span className="truncate">{srv.service}</span>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 text-right tabular-nums text-slate-400 text-[13px]">
                                    {fmtUSD(srv.awsBaseCost)}
                                  </td>
                                  <td className={`px-6 py-4 text-right tabular-nums text-[13px] ${srv.awsSavings > 0 ? "text-emerald-400" : "text-slate-600"}`}>
                                    {srv.awsSavings > 0 ? `${fmtUSD(srv.awsSavings)}` : "-"}
                                  </td>
                                  <td className={`px-6 py-4 text-right tabular-nums text-[13px] ${srv.awsCredits > 0 ? "text-purple-400" : "text-slate-600"}`}>
                                    {srv.awsCredits > 0 ? `${fmtUSD(srv.awsCredits)}` : "-"}
                                  </td>
                                  <td className="px-6 py-4 text-right font-bold tabular-nums text-slate-300 text-[13px]">
                                    {fmtUSD(srv.awsCost)}
                                  </td>
                                </tr>
                              ))}
                            </React.Fragment>
                          )}

                        </tbody>
                      </table>
                    </motion.div>
                  )}
                </AnimatePresence>
              </section>
            )}
            */}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

export default DashboardModal;